import { IonLabel, IonList, IonListHeader, IonText } from '@ionic/react';
import { sortBy } from 'lodash';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Application,
  ApplicationLicenseType,
  useListApplicationByTypeQuery,
  useListApplicationLicenseQuery,
} from '../../api/graphql/generated';
import { useBeaconRouter } from '../../route/route.hooks';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { IonItem } from '../ionic/IonItem';

interface ApplicationsListProps {
  teamId: string;
  machineId: string;
}

const ApplicationsList = (props: ApplicationsListProps): ReactElement => {
  const { teamId, machineId } = props;
  const { i18n } = useTranslation();

  const {
    data: availableApplications,
    loading,
    error,
    refetch,
  } = useListApplicationByTypeQuery({
    variables: {
      input: {
        licenseType: ApplicationLicenseType.MACHINE,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: assignedLicenses } = useListApplicationLicenseQuery({
    variables: {
      input: {
        assignedTo: machineId,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const assignedApplications = useMemo(() => {
    if (assignedLicenses?.listApplicationLicenses.items) {
      return assignedLicenses.listApplicationLicenses.items.map((license) => {
        return license.applicationVersion.application || undefined;
      });
    }
  }, [assignedLicenses]);

  const assignedApplicationMap: Record<string, boolean> = useMemo(() => {
    return (
      assignedApplications?.reduce(
        (m, app) => {
          if (app) {
            m[app.id] = true;
          }
          return m;
        },
        {} as Record<string, boolean>
      ) || {}
    );
  }, [assignedApplications]);

  const filteredAvailableApplications = useMemo(() => {
    return (
      availableApplications?.listApplicationsByType.items?.filter((item) => {
        return (
          item.maxLicenseCount - (item.usedLicenseCount ?? 0) > 0 &&
          !assignedApplicationMap[item.id]
        );
      }) || []
    );
  }, [
    assignedApplicationMap,
    availableApplications?.listApplicationsByType.items,
  ]);

  const countOfTotalAppsAvailable =
    availableApplications?.listApplicationsByType.total ?? 0;

  return (
    <LoadingRetry
      loading={loading}
      error={error}
      refetch={refetch}
      results={{
        hasResults: countOfTotalAppsAvailable > 0,
        noResultsComponent: (
          <div className="ion-padding ion-text-center">
            <IonText color="medium">
              {i18n.t('common:applications_none_available')}
            </IonText>
          </div>
        ),
      }}
    >
      <ApplicationSection
        label={i18n.t('common:assigned_applications')}
        applications={assignedApplications || []}
        teamId={teamId}
        machineId={machineId}
      />
      <ApplicationSection
        label={i18n.t('common:available_applications')}
        applications={filteredAvailableApplications}
        teamId={teamId}
        machineId={machineId}
      />
    </LoadingRetry>
  );
};

type ApplicationListItem = Omit<Application, 'versions'>; // TODO: need to figure out why versions is giving an issue

interface ApplicationSectionProps {
  label: string;
  applications: (ApplicationListItem | undefined)[];
  teamId: string;
  machineId: string;
  preventNavigation?: boolean;
}

const ApplicationSection = (
  props: ApplicationSectionProps
): ReactElement | null => {
  const { applications, label, teamId, machineId, preventNavigation } = props;
  const filteredApplications = applications.filter(
    (l) => l !== undefined
  ) as ApplicationListItem[];
  const sortedApplications = sortBy(filteredApplications, ['name']);

  if (sortedApplications.length !== 0) {
    return (
      <>
        <IonListHeader>
          <IonLabel>{label}</IonLabel>
        </IonListHeader>
        <IonList lines="full">
          {sortedApplications.map((application) => {
            return (
              <ApplicationItem
                key={application.id}
                application={application}
                teamId={teamId}
                machineId={machineId}
                preventNavigation={preventNavigation}
              />
            );
          })}
        </IonList>
      </>
    );
  } else {
    return null;
  }
};

interface ApplicationItemProps {
  application: ApplicationListItem;
  teamId: string;
  machineId: string;
  preventNavigation?: boolean;
}

const ApplicationItem = (props: ApplicationItemProps): ReactElement => {
  const { application, teamId, machineId, preventNavigation } = props;

  const { builder } = useBeaconRouter();

  return (
    <IonItem
      routerLink={
        preventNavigation
          ? undefined
          : builder.getMachineApplicationRoute(
              teamId,
              machineId,
              application.id
            )
      }
    >
      <IonLabel>{application.name}</IonLabel>
    </IonItem>
  );
};

export { ApplicationsList };
